// TEMP(debug): GitHub-style before/after diff of the dashboard spec for a turn.
// Remove together with the raw-response debug plumbing.
import React, { useMemo, useState } from "react";
import { Modal } from "antd";

// ── Canonicalization ─────────────────────────────────────────
// The "before" (FE-serialized clientSpec) and "after" (backend validated dump)
//  differ in key order and in default noise (null fields, empty arrays/objects the
// backend dump materializes). Sort keys and drop empties on BOTH sides so the diff
// shows real changes. Heuristic — fine for a dev tool, not for data.
function sortValue(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sortValue);
  if (v && typeof v === "object") {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(v as Record<string, unknown>).sort()) {
      const s = sortValue((v as Record<string, unknown>)[k]);
      if (s === null || s === undefined) continue;
      if (Array.isArray(s) && s.length === 0) continue;
      if (s && typeof s === "object" && !Array.isArray(s) && Object.keys(s).length === 0) continue;
      out[k] = s;
    }
    return out;
  }
  return v;
}

export function canonicalJson(value: unknown): string {
  return JSON.stringify(sortValue(value), null, 2);
}

// ── Line diff (LCS with prefix/suffix trim + hunks) ──────────
export interface DiffRow {
  type: "ctx" | "add" | "del" | "gap";
  text: string;
  oldNo: number | null;
  newNo: number | null;
  hidden?: number;
}

interface Op { type: "ctx" | "add" | "del"; text: string; oldNo: number | null; newNo: number | null; }

function diffOps(a: string[], b: string[]): Op[] {
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start += 1;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) { endA -= 1; endB -= 1; }

  const ops: Op[] = [];
  for (let i = 0; i < start; i += 1) ops.push({ type: "ctx", text: a[i], oldNo: i + 1, newNo: i + 1 });

  const midA = a.slice(start, endA);
  const midB = b.slice(start, endB);

  if (midA.length * midB.length > 4_000_000) {
    // Too big for DP — degrade to a blunt replace block.
    midA.forEach((t, i) => ops.push({ type: "del", text: t, oldNo: start + i + 1, newNo: null }));
    midB.forEach((t, i) => ops.push({ type: "add", text: t, oldNo: null, newNo: start + i + 1 }));
  } else if (midA.length || midB.length) {
    const n = midA.length;
    const m = midB.length;
    const width = m + 1;
    const lcs = new Uint32Array((n + 1) * width);
    for (let i = n - 1; i >= 0; i -= 1) {
      for (let j = m - 1; j >= 0; j -= 1) {
        lcs[i * width + j] =
          midA[i] === midB[j]
            ? lcs[(i + 1) * width + j + 1] + 1
            : Math.max(lcs[(i + 1) * width + j], lcs[i * width + j + 1]);
      }
    }
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
      if (midA[i] === midB[j]) {
        ops.push({ type: "ctx", text: midA[i], oldNo: start + i + 1, newNo: start + j + 1 });
        i += 1; j += 1;
      } else if (lcs[(i + 1) * width + j] >= lcs[i * width + j + 1]) {
        ops.push({ type: "del", text: midA[i], oldNo: start + i + 1, newNo: null });
        i += 1;
      } else {
        ops.push({ type: "add", text: midB[j], oldNo: null, newNo: start + j + 1 });
        j += 1;
      }
    }
    while (i < n) { ops.push({ type: "del", text: midA[i], oldNo: start + i + 1, newNo: null }); i += 1; }
    while (j < m) { ops.push({ type: "add", text: midB[j], oldNo: null, newNo: start + j + 1 }); j += 1; }
  }

  for (let i = endA; i < a.length; i += 1) {
    const offset = i - endA;
    ops.push({ type: "ctx", text: a[i], oldNo: i + 1, newNo: endB + offset + 1 });
  }
  return ops;
}

/** Collapse long unchanged runs into gap rows, keeping `context` lines around changes. */
export function computeDiffRows(beforeText: string, afterText: string, context = 3): DiffRow[] {
  const ops = diffOps(beforeText.split("\n"), afterText.split("\n"));
  const keep = new Array<boolean>(ops.length).fill(false);
  ops.forEach((op, idx) => {
    if (op.type === "ctx") return;
    for (let k = Math.max(0, idx - context); k <= Math.min(ops.length - 1, idx + context); k += 1) keep[k] = true;
  });

  const rows: DiffRow[] = [];
  let hidden = 0;
  const flushGap = () => {
    if (hidden > 0) {
      rows.push({ type: "gap", text: `··· ${hidden} unchanged line${hidden === 1 ? "" : "s"} ···`, oldNo: null, newNo: null, hidden });
      hidden = 0;
    }
  };
  ops.forEach((op, idx) => {
    if (op.type === "ctx" && !keep[idx]) { hidden += 1; return; }
    flushGap();
    rows.push({ type: op.type, text: op.text, oldNo: op.oldNo, newNo: op.newNo });
  });
  flushGap();
  return rows;
}

// ── Component ────────────────────────────────────────────────
const CompareDiff: React.FC<{ before: string; after: string }> = ({ before, after }) => {
  const [open, setOpen] = useState(false);

  const { rows, added, removed } = useMemo(() => {
    if (!open) return { rows: [] as DiffRow[], added: 0, removed: 0 };
    const computed = computeDiffRows(before, after);
    return {
      rows: computed,
      added: computed.filter((r) => r.type === "add").length,
      removed: computed.filter((r) => r.type === "del").length,
    };
  }, [open, before, after]);

  return (
    <>
      <button
        type="button"
        className="sandbox-ai-debug-copy"
        onClick={() => setOpen(true)}
        title="Show the spec changes this turn made (debug)"
      >
        Compare diff
      </button>
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={760}
        title={`Dashboard spec diff (+${added} −${removed})`}
        className="sandbox-ai-diff-modal"
      >
        <div className="sandbox-ai-diff-body">
          {rows.map((row, idx) => (
            <div key={idx} className={`sandbox-ai-diff-row sandbox-ai-diff-${row.type}`}>
              <span className="sandbox-ai-diff-lineno">{row.oldNo ?? ""}</span>
              <span className="sandbox-ai-diff-lineno">{row.newNo ?? ""}</span>
              <span className="sandbox-ai-diff-sign">
                {row.type === "add" ? "+" : row.type === "del" ? "−" : " "}
              </span>
              <span className="sandbox-ai-diff-text">{row.text}</span>
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
};

export default CompareDiff;
